/*
 * Copyright (C) 2024 Das Land Schleswig-Holstein vertreten durch den
 * Ministerpräsidenten des Landes Schleswig-Holstein
 * Staatskanzlei
 * Abteilung Digitalisierung und zentrales IT-Management der Landesregierung
 *
 * Lizenziert unter der EUPL, Version 1.2 oder - sobald
 * diese von der Europäischen Kommission genehmigt wurden -
 * Folgeversionen der EUPL ("Lizenz");
 * Sie dürfen dieses Werk ausschließlich gemäß
 * dieser Lizenz nutzen.
 * Eine Kopie der Lizenz finden Sie hier:
 *
 * https://joinup.ec.europa.eu/collection/eupl/eupl-text-eupl-12
 *
 * Sofern nicht durch anwendbare Rechtsvorschriften
 * gefordert oder in schriftlicher Form vereinbart, wird
 * die unter der Lizenz verbreitete Software "so wie sie
 * ist", OHNE JEGLICHE GEWÄHRLEISTUNG ODER BEDINGUNGEN -
 * ausdrücklich oder stillschweigend - verbreitet.
 * Die sprachspezifischen Genehmigungen und Beschränkungen
 * unter der Lizenz sind dem Lizenztext zu entnehmen.
 */
import { getUserName, getUserNameInitials, UserProfileResource } from '@alfa-client/user-profile-shared';
import { inject, Injectable } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Environment, ENVIRONMENT_CONFIG } from '@common/environment-shared';
import { AuthConfig, OAuthEvent, OAuthService } from 'angular-oauth2-oidc';
import { jwtDecode } from 'jwt-decode';
import { filter, firstValueFrom, Subscription } from 'rxjs';
import {
  AccessTokenClaims,
  AuthenticationConfig,
  CONFIGURE_TOTP_ACTION,
  IdentityClaims,
  UPDATE_PASSWORD_ACTION,
} from './authentication.model';

@Injectable({ providedIn: 'root' })
export class AuthenticationService {
  private readonly oAuthService = inject(OAuthService);
  private readonly router = inject(Router);
  private readonly envConfig = inject<Environment>(ENVIRONMENT_CONFIG);

  currentUserResource: UserProfileResource;

  private eventSubscription: Subscription;

  public async login(config: AuthenticationConfig): Promise<void> {
    if (config.waitForInitialNavgiationToComplete) await this._initialNavigationIsDone();
    this.oAuthService.configure(this._buildConfiguration(config.redirectUriSuffix));
    this.oAuthService.setupAutomaticSilentRefresh();

    const eventPromise: Promise<void> = this._buildAuthEventPromise();
    await this.oAuthService.loadDiscoveryDocumentAndLogin();
    return eventPromise;
  }

  async _initialNavigationIsDone(): Promise<void> {
    await firstValueFrom(this.router.events.pipe(filter((event) => event instanceof NavigationEnd)));
  }

  _buildAuthEventPromise(): Promise<void> {
    return new Promise<void>((resolve, reject) => this.handleAuthEventsForPromise(resolve, reject));
  }

  private handleAuthEventsForPromise(resolve: (value: void | PromiseLike<void>) => void, reject: (reason?: any) => void): void {
    this.eventSubscription = this.oAuthService.events
      .pipe(filter((event: OAuthEvent) => this._shouldProceedByAuthEvent(event)))
      .subscribe({
        next: () => {
          this._setCurrentUser();
          this._unsubscribeEvents();
          resolve();
        },
        error: (error: any) => {
          this._unsubscribeEvents();
          reject(error);
        },
      });
  }

  _shouldProceedByAuthEvent(event: OAuthEvent): boolean {
    return this._consideredAsLoginEvent(event.type) || this._consideredAsPageReloadEvent(event.type);
  }

  _consideredAsLoginEvent(eventType: string): boolean {
    return eventType === 'token_received';
  }

  _consideredAsPageReloadEvent(eventType: string): boolean {
    return eventType === 'discovery_document_loaded' && this._hasValidToken();
  }

  _hasValidToken(): boolean {
    return this.oAuthService.hasValidAccessToken() && this.oAuthService.hasValidIdToken();
  }

  _buildConfiguration(redirectUriSuffix: string): AuthConfig {
    const realmBaseUrl = `${this.envConfig.authServer}/realms/${this.envConfig.realm}`;
    return {
      issuer: realmBaseUrl,
      loginUrl: `${realmBaseUrl}/protocol/openid-connect/auth`,
      tokenEndpoint: `${realmBaseUrl}/protocol/openid-connect/token`,
      logoutUrl: `${realmBaseUrl}/protocol/openid-connect/logout`,
      redirectUri: window.location.origin + redirectUriSuffix,
      clientId: this.envConfig.clientId,
      scope: 'openid profile',
      requireHttps: false,
      responseType: 'code',
      showDebugInformation: false,
      postLogoutRedirectUri: `${window.location.origin}/`,
    };
  }

  public updatePassword(redirectPath: string): void {
    this._startAccountAction(UPDATE_PASSWORD_ACTION, redirectPath);
  }

  public configureTotp(redirectPath: string): void {
    this._startAccountAction(CONFIGURE_TOTP_ACTION, redirectPath);
  }

  _startAccountAction(action: string, redirectPath: string): void {
    this.oAuthService.configure(this._buildConfiguration('/' + redirectPath));
    this.oAuthService.initLoginFlow(undefined, { kc_action: action });
  }

  _setCurrentUser(): void {
    const claims: IdentityClaims = this.getIdentityClaims();
    const userResource: UserProfileResource = <any>{
      firstName: claims.given_name,
      lastName: claims.family_name,
    };
    this.currentUserResource = userResource;
  }

  public getIdentityClaims(): IdentityClaims {
    return this.oAuthService.getIdentityClaims() as IdentityClaims;
  }

  public getAccessTokenClaims(): AccessTokenClaims {
    return jwtDecode<AccessTokenClaims>(this.oAuthService.getAccessToken());
  }

  _unsubscribeEvents(): void {
    this.eventSubscription.unsubscribe();
  }

  public getCurrentUserInitials(): string {
    return getUserNameInitials(this.currentUserResource);
  }

  public getCurrentUserName(): string {
    return getUserName(this.currentUserResource);
  }

  public logout(): void {
    this.oAuthService.logOut();
  }

  public isLoggedIn(): boolean {
    return this.oAuthService.hasValidAccessToken();
  }
}
